/**
 * GitHub API Utility Class
 * Centralizes all GitHub API interactions for the portfolio website
 * 
 * Consolidates functionality from:
 * - Projects.tsx: fetchRepoTopics, fetchRepoDescription
 * - GitHubReadme.tsx: extractRepoInfo, fetchReadme
 */

export interface GitHubRepoInfo {
  owner: string;
  repo: string;
}

export interface GitHubContentResponse {
  content: string;
  encoding: string;
  name: string;
  path: string;
}

export interface GitHubTopicsResponse {
  names: string[];
}

export interface GitHubRepositoryResponse {
  name: string;
  description: string | null;
  default_branch: string;
  owner: {
    login: string;
  };
}

export class GithubInteraction {
  private readonly baseUrl = 'https://api.github.com';
  private readonly token: string | undefined;

  constructor() {
    this.token = import.meta.env.VITE_GITHUB_ACCESS_TOKEN_SECRET;
  }

  /**
   * Standardized URL parsing method
   * Combines logic from both Projects.tsx and GitHubReadme.tsx
   * 
   * Projects.tsx used: projectURL.split('/').slice(-2).join('/')
   * GitHubReadme.tsx used: regex /github\.com\/([^/]+)\/([^/]+)/
   */
  public static parseGitHubUrl(url: string): GitHubRepoInfo | null {
    try {
      // Use regex approach from GitHubReadme.tsx (more robust)
      const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
      if (match) {
        return { 
          owner: match[1], 
          repo: match[2] 
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to parse GitHub URL:', error);
      return null;
    }
  }

  /**
   * Extract owner/repo string in format used by Projects.tsx
   * For backwards compatibility with existing URL handling
   */
  public static extractRepoPath(url: string): string | null {
    const repoInfo = this.parseGitHubUrl(url);
    if (repoInfo) {
      return `${repoInfo.owner}/${repoInfo.repo}`;
    }
    return null;
  }

  /**
   * Common headers and authentication setup
   * Consolidates header logic from both Projects.tsx and GitHubReadme.tsx
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Accept': 'application/vnd.github+json',
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Generic method to make GitHub API requests with consistent error handling.
   * Retries once on a 429 (rate limited), honoring the Retry-After header when
   * GitHub provides one, since unauthenticated requests are prone to hitting
   * GitHub's secondary rate limit under bursts of concurrent calls.
   */
  private async makeRequest<T>(endpoint: string, retriesLeft = 1): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (response.status === 429 && retriesLeft > 0) {
        const retryAfterSeconds = Number(response.headers.get('Retry-After'));
        const backoffMs = retryAfterSeconds > 0 ? retryAfterSeconds * 1000 : 2000;
        await this.sleep(backoffMs);
        return this.makeRequest<T>(endpoint, retriesLeft - 1);
      }

      if (!response.ok) {
        throw new Error(`GitHub API Error: ${response.status} - ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`GitHub API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Repository topics fetching method
   * Replaces fetchRepoTopics from Projects.tsx
   */
  public async getRepositoryTopics(url: string): Promise<string[]> {
    try {
      const repoPath = GithubInteraction.extractRepoPath(url);
      if (!repoPath) {
        throw new Error('Invalid GitHub URL');
      }

      const data = await this.makeRequest<GitHubTopicsResponse>(`/repos/${repoPath}/topics`);
      return data.names || [];
    } catch (error) {
      console.error(`Failed to fetch repository topics for ${url}:`, error);
      return []; // Return empty array on error (matches original behavior)
    }
  }

  /**
   * Repository description fetching method
   * Replaces fetchRepoDescription from Projects.tsx
   */
  public async getRepositoryDescription(url: string): Promise<string> {
    try {
      const repoPath = GithubInteraction.extractRepoPath(url);
      if (!repoPath) {
        throw new Error('Invalid GitHub URL');
      }

      const data = await this.makeRequest<GitHubRepositoryResponse>(`/repos/${repoPath}`);
      return data.description || '';
    } catch (error) {
      console.error(`Failed to fetch repository description for ${url}:`, error);
      return ''; // Return empty string on error (matches original behavior)
    }
  }

  /**
   * Base64 decoding and UTF-8 conversion utility
   * Extracted from GitHubReadme.tsx fetchReadme method
   */
  private decodeBase64Content(base64Content: string): string {
    try {
      // Remove whitespace from base64 content
      const cleanBase64 = base64Content.replace(/\s/g, '');
      
      // Decode base64 to binary string
      const binaryString = atob(cleanBase64);
      
      // Convert binary string to UTF-8
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
      console.error('Failed to decode base64 content:', error);
      throw new Error('Failed to decode README content');
    }
  }

  /**
   * README content fetching with multi-fallback logic
   * Replaces fetchReadme from GitHubReadme.tsx
   */
  public async getReadmeContent(url: string): Promise<string> {
    const repoInfo = GithubInteraction.parseGitHubUrl(url);
    if (!repoInfo) {
      throw new Error('Invalid GitHub URL');
    }

    const { owner, repo } = repoInfo;
    
    // Try different README file names and branches (from original implementation)
    const readmeFiles = ['README.md', 'readme.md', 'Readme.md', 'README.MD'];
    const branches = ['main', 'master'];

    for (const branch of branches) {
      for (const filename of readmeFiles) {
        try {
          const data = await this.makeRequest<GitHubContentResponse>(
            `/repos/${owner}/${repo}/contents/${filename}?ref=${branch}`
          );
          
          if (data.encoding === 'base64') {
            return this.decodeBase64Content(data.content);
          } else {
            return data.content;
          }
        } catch (error) {
          // Continue trying other files/branches
          continue;
        }
      }
    }

    throw new Error('README file not found');
  }

  /**
   * Batch operations for multiple repositories
   * Replaces the batch fetching logic from Projects.tsx useEffect
   */
  public async batchGetRepositoryData(urls: string[]): Promise<{
    topics: { [url: string]: string[] };
    descriptions: { [url: string]: string };
  }> {
    const topics: { [url: string]: string[] } = {};
    const descriptions: { [url: string]: string } = {};

    // Process a few repositories at a time rather than all at once -- firing
    // 2 requests per repo for every project simultaneously (20+ concurrent
    // unauthenticated requests) reliably trips GitHub's secondary rate limit.
    const CONCURRENCY_LIMIT = 3;
    for (let i = 0; i < urls.length; i += CONCURRENCY_LIMIT) {
      const batch = urls.slice(i, i + CONCURRENCY_LIMIT);
      await Promise.all(batch.map(async (url) => {
        try {
          const [topicsResult, descriptionResult] = await Promise.all([
            this.getRepositoryTopics(url),
            this.getRepositoryDescription(url)
          ]);

          topics[url] = topicsResult;
          descriptions[url] = descriptionResult;
        } catch (error) {
          console.error(`Failed to fetch data for ${url}:`, error);
          // Set fallback values on error
          topics[url] = [];
          descriptions[url] = '';
        }
      }));
    }

    return { topics, descriptions };
  }

  /**
   * Convenience method that combines topics and description fetching for a single repository
   */
  public async getRepositoryData(url: string): Promise<{
    topics: string[];
    description: string;
  }> {
    const [topics, description] = await Promise.all([
      this.getRepositoryTopics(url),
      this.getRepositoryDescription(url)
    ]);

    return { topics, description };
  }
}

// Export a default instance for easy use
export default new GithubInteraction();